// What Greenback Business costs, held once per page load (PRD 86 slice 4), in
// billingAccountsStore's idiom: a module store, because the price list is the
// same for everybody and every screen that names the sale (/business/start
// now, the sale's buttons in the launch PR) reads one answer.
//
// ONE REQUEST PER PAGE LOAD. The first subscriber asks; everyone else reads
// the answer. The api caches it publicly for five minutes anyway.
//
// WHY THERE IS NO ANSWER, kept as `reason`:
// - `not_served`: the api says there is no price list to sell from. A 404 is
//   an api before slice 4; a 503 `pricing_unavailable` is one with no
//   published book. Asking again would not change either, so it never retries.
// - `failed`: the network, a timeout, a 5xx. Retried once, silently, a second
//   later; then the screen says it couldn't load prices, with Retry
//   (`retryPublicPricing`).
//
// NEVER FAILS OPEN. Nothing here says Business is on sale: useBusinessOnSale
// reads `on_sale` and the base line from a `ready` answer only, so a failure,
// a loading state and an older api all read as "not on sale".

import { BusinessApiError } from './businessApiCommon';
import { getPublicPricing } from './publicPricingService';
import { PublicPricing } from '../types/publicPricing';

export interface PublicPricingState {
  status: 'idle' | 'loading' | 'ready' | 'unavailable';
  data: PublicPricing | null;
  reason?: 'not_served' | 'failed';
}

/** Tunable for tests: the pause before the one silent retry, and how long one ask may take. */
export const PUBLIC_PRICING_TIMING = { retryMs: 1000, timeoutMs: 10000 };

const EMPTY: PublicPricingState = { status: 'idle', data: null };

type Listener = (state: PublicPricingState) => void;

let state: PublicPricingState = EMPTY;
// Bumped by every reset and retry, so a stale answer is dropped.
let generation = 0;
const listeners = new Set<Listener>();

function publish(next: PublicPricingState): void {
  state = next;
  listeners.forEach(listener => listener(state));
}

export function getPublicPricingState(): PublicPricingState {
  return state;
}

export function subscribePublicPricing(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The api has no price list to sell from: an api before slice 4, or no published book. */
const isNotServed = (error: unknown): boolean =>
  error instanceof BusinessApiError
  && (error.status === 404 || (error.status === 503 && error.code === 'pricing_unavailable'));

async function askOnce(): Promise<PublicPricing> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PUBLIC_PRICING_TIMING.timeoutMs);
  try {
    return await getPublicPricing({ signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function ask(): Promise<void> {
  const asked = generation;
  publish({ status: 'loading', data: null });
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const data = await askOnce();
      if (asked !== generation) return;
      publish({ status: 'ready', data });
      return;
    } catch (error) {
      if (asked !== generation) return;
      if (isNotServed(error)) {
        publish({ status: 'unavailable', data: null, reason: 'not_served' });
        return;
      }
      if (attempt === 0) {
        await new Promise(resolve => setTimeout(resolve, PUBLIC_PRICING_TIMING.retryMs));
        if (asked !== generation) return;
      }
    }
  }
  publish({ status: 'unavailable', data: null, reason: 'failed' });
}

/** Ask once per page load; a second caller reuses the answer. */
export function ensurePublicPricingLoaded(): void {
  if (state.status === 'idle') void ask();
}

/** "Retry": forget the failure and ask again. */
export function retryPublicPricing(): void {
  generation += 1;
  void ask();
}

/** Tests only: forget everything, listeners included. */
export function resetPublicPricingStore(): void {
  generation += 1;
  state = EMPTY;
  listeners.clear();
}

/**
 * Whether Greenback Business is on sale: a price list arrived, its switch is
 * on, and its base line is priced and linked to Stripe. False while loading,
 * on any failure, and from an api that does not serve the list.
 */
export function isBusinessOnSale(current: PublicPricingState): boolean {
  const { status, data } = current;
  return status === 'ready'
    && !!data
    && data.on_sale === true
    && !!data.base
    && data.base.sellable === true
    && typeof data.base.amount_cents === 'number';
}
